import { useChatContext, type Conversation } from '../hooks/useChat';

interface HistoryViewProps {
  onSelectConversation: () => void;
  onNewChat: () => void;
}

export default function HistoryView({ onSelectConversation, onNewChat }: HistoryViewProps) {
  const { conversations, selectConversation } = useChatContext();

  const handleSelect = (conversation: Conversation) => {
    selectConversation(conversation.id);
    onSelectConversation();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'long' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-gray-400"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No conversations yet
        </h3>

        <p className="text-gray-500 text-sm mb-6 max-w-xs">
          Start a new conversation and it will appear here.
        </p>

        <button
          onClick={onNewChat}
          className="px-6 py-2.5 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
        >
          Start chatting
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-900">Conversation History</h2>
        <p className="text-xs text-gray-500">{conversations.length} conversation{conversations.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {conversations.map((conversation) => (
          <button
            key={conversation.id}
            onClick={() => handleSelect(conversation)}
            className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors border-b border-gray-50 text-left"
          >
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-primary"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {conversation.title || 'Conversation'}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {formatDate(conversation.updatedAt)}
                </span>
              </div>

              <p className="text-xs text-gray-500 truncate">
                {conversation.lastMessagePreview || 'No messages'}
              </p>

              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-gray-400">
                  {conversation.messageCount} message{conversation.messageCount !== 1 ? 's' : ''}
                </span>
                {conversation.status === 'ended' && (
                  <span className="text-[10px] text-gray-400 px-1.5 py-0.5 bg-gray-100 rounded">
                    Ended
                  </span>
                )}
              </div>
            </div>

            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="text-gray-300 flex-shrink-0 mt-2"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        ))}
      </div>

      <div className="p-4 border-t border-gray-100">
        <button
          onClick={onNewChat}
          className="w-full py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-xl transition-colors"
        >
          + Start new conversation
        </button>
      </div>
    </div>
  );
}
